import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Input,
  Label,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import AttachmentGallery from './AttachmentGallery';

const RECEIPT_KIND = 'tonnage_receipt';

/** A weighed container line on the order (roll-off / front-loader). */
export interface WeighedLine {
  /** Booqable line id. */
  id: string;
  /** Display label (the line/product name). */
  label: string;
}

/** Per-line tonnage row as returned by GET /orders/:id/line-tonnage. */
interface LineTonnageRow {
  line_id: string;
  net_tons: number | null;
}

/** Shape of POST /orders/:id/tonnage's response. Surfaced to OrderDetails so it
 *  can render the reconciled overage line optimistically — straight from this
 *  payload — instead of waiting on a full order refetch. */
export interface TonnageOverageSummary {
  /** The weighed line this save reconciled. */
  line_id: string;
  net_tons: number | null;
  included_tons: number;
  overage_tons: number;
  overage_in_cents: number;
  overage_line_action: 'created' | 'removed' | 'none' | 'skipped_unpromoted';
  overage_line_id: string | null;
  /** The overage line id this save replaced (a re-save mints a new id), so the
   *  caller can drop exactly that row without disturbing other lines' overage. */
  previous_overage_line_id: string | null;
}

const useStyles = makeStyles({
  lineCard: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lineTitle: {
    fontSize: '13px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  // Inline net field: a touch wider than the "Add receipt" button above it,
  // rather than stretching the full panel width.
  netInputInline: {
    width: '180px',
    maxWidth: '100%',
  },
  fieldLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  saveRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  // Inline under a product row the Save button lines up with the left edge of
  // the net field above it, rather than floating to the panel's right edge.
  saveRowInline: {
    justifyContent: 'flex-start',
  },
  // Inline variant (rendered under a product line row): no card chrome, just a
  // light left rule so it reads as a child of the line above it.
  lineCardInline: {
    ...shorthands.borderRadius('0'),
    ...shorthands.border('0'),
    ...shorthands.borderLeft('2px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('8px', '0', '12px', '12px'),
    backgroundColor: 'transparent',
  },
});

/** Per-line net figures for an order, keyed by Booqable line id. Shared by the
 *  sidebar Tonnage tab and the inline per-line panels in the Products list. */
export function useLineTonnageNet(orderId: string | null): Map<string, number | null> {
  const query = useQuery<{ data: LineTonnageRow[] }>({
    queryKey: ['order-line-tonnage', orderId],
    queryFn: async () => {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderId!)}/line-tonnage`);
      if (!res.ok) throw new Error(`line-tonnage fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 60 * 1000,
  });
  return useMemo(() => {
    const map = new Map<string, number | null>();
    for (const row of query.data?.data ?? []) map.set(row.line_id, row.net_tons);
    return map;
  }, [query.data]);
}

// Trim to a tidy numeric string ("" when not a finite number).
const numToStr = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? '' : String(n);

const parseNum = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

interface LineTonnageCardProps {
  orderId: string;
  line: WeighedLine;
  /** Server-stored net tons for this line (null when never set). */
  savedNet: number | null;
  /** Render without card chrome (used inline under a Products line row). */
  inline?: boolean;
  onLinesChanged?: (summary?: TonnageOverageSummary) => void;
  /** Customer name for the order — used to name uploaded receipts
   *  "[Customer] - [Line] - Tonnage Receipt". */
  customerName?: string | null;
}

// One weighed line: its net-tons entry + Save, plus a receipt gallery scoped to
// the line. Net state seeds from the server figure and re-seeds if it changes.
export const LineTonnageCard: React.FC<LineTonnageCardProps> = ({ orderId, line, savedNet, inline, onLinesChanged, customerName }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('tonnage-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [net, setNet] = useState(numToStr(savedNet));
  const savedRef = useRef<string>(numToStr(savedNet));
  const [saving, setSaving] = useState(false);

  // Re-seed when the persisted figure resolves/changes (but not while the user
  // is mid-edit — only when our last-saved baseline still matches the field).
  useEffect(() => {
    const next = numToStr(savedNet);
    if (savedRef.current === net) {
      setNet(next);
    }
    savedRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedNet]);

  const dirty = useMemo(() => savedRef.current !== net, [net]);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/tonnage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line_id: line.id,
          line_label: line.label,
          net_tons: parseNum(net),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      const summary = (await res.json().catch(() => ({}))) as TonnageOverageSummary;
      savedRef.current = net;
      // Refresh the per-line figures + the documents family (the overage line
      // changed the order's invoice/lines).
      queryClient.invalidateQueries({ queryKey: ['order-line-tonnage', orderId] });
      queryClient.invalidateQueries({ queryKey: ['order-documents'] });
      onLinesChanged?.(summary);
    } catch (err) {
      dispatchToast(
        <Toast>
          <ToastTitle>Could not save tonnage</ToastTitle>
          <ToastBody>{err instanceof Error ? err.message : 'Unknown error'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={inline ? `${styles.lineCard} ${styles.lineCardInline}` : styles.lineCard}>
      {/* Inline under a product row the line name is already shown above, so the
          header is only rendered for the standalone (sidebar) card. */}
      {!inline && <div className={styles.lineTitle}>{line.label}</div>}

      {/* Receipt scan(s) for this container — same upload/drag UX as before,
          now filed under the line so the Send dialog can list them per-line. */}
      <AttachmentGallery
        orderId={orderId}
        kind={RECEIPT_KIND}
        noun="receipt"
        acceptPdf
        hideEmptyHint={inline}
        lineId={line.id}
        lineLabel={line.label}
        customerName={customerName}
      />

      <div className={styles.field}>
        <Label className={styles.fieldLabel}>Net weight (tons)</Label>
        <Input
          size="large"
          className={inline ? styles.netInputInline : undefined}
          type="number"
          // step="0.01" matches the Numeric(10,2) column and stops the browser
          // from treating decimals as invalid (default step=1 flags "1.5").
          step="0.01"
          min={0}
          inputMode="decimal"
          value={net}
          onChange={(_, d) => setNet(d.value)}
          placeholder="0.00"
        />
      </div>

      <div className={inline ? `${styles.saveRow} ${styles.saveRowInline}` : styles.saveRow}>
        <Button appearance="primary" disabled={!dirty || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <Toaster toasterId={toasterId} position="top" />
    </div>
  );
};

